/**
 * K-12 districts domain — React Query hooks.
 *
 * Read hooks are re-exported directly from the generated client (with a
 * couple of hand-written wrappers where drf-spectacular's auto-wrapping
 * produces the wrong response type, or where the legacy hook shape
 * relied on a 404-skipping retry predicate).
 *
 * Write hooks go through `useInvalidatingMutation` so cache
 * invalidation is declarative and compile-time-enforced via the
 * `K12MutationId` registry in `./invalidation`.
 */

import { useQuery, type UseQueryOptions } from '@tanstack/react-query'
import axios, { AxiosError } from 'axios'

import { useInvalidatingMutation } from '../invalidation/useInvalidatingMutation'
import { invalidationRegistry } from './invalidation'

// ── Generated query-hook re-exports ────────────────────────────────

export {
  useListK12Districts,
  listK12DistrictsQueryKey,
  useGetK12DistrictsSummary,
  getK12DistrictsSummaryQueryKey,
  getK12DistrictQueryKey,
  useListK12DistrictsCounties,
  listK12DistrictsCountiesQueryKey,
} from '../generated/hooks/k12Controller'

// ── Generated client-function re-exports (imperative consumers) ────

export { listK12Districts } from '../generated/clients/listK12Districts'
export { getK12DistrictsSummary } from '../generated/clients/getK12DistrictsSummary'

// ── Generated query/mutation type re-exports ───────────────────────

export type {
  ListK12DistrictsQueryResponse,
  ListK12DistrictsQueryParams,
} from '../generated/types/ListK12Districts'
export type {
  GetK12DistrictQueryResponse,
  GetK12DistrictPathParams,
} from '../generated/types/GetK12District'
export type { GetK12DistrictsSummaryQueryResponse } from '../generated/types/GetK12DistrictsSummary'
export type {
  BulkTagsK12DistrictsMutationRequest,
  BulkTagsK12DistrictsMutationResponse,
} from '../generated/types/BulkTagsK12Districts'
export type {
  BulkDistributorsK12DistrictsMutationRequest,
  BulkDistributorsK12DistrictsMutationResponse,
} from '../generated/types/BulkDistributorsK12Districts'
export type {
  BulkGposK12DistrictsMutationRequest,
  BulkGposK12DistrictsMutationResponse,
} from '../generated/types/BulkGposK12Districts'
export type {
  BulkDealsK12DistrictsMutationRequest,
  BulkDealsK12DistrictsMutationResponse,
} from '../generated/types/BulkDealsK12Districts'
export type {
  AssignK12DistrictsToCampaignMutationRequest,
  AssignK12DistrictsToCampaignMutationResponse,
} from '../generated/types/AssignK12DistrictsToCampaign'
export type {
  ExportK12DistrictsV2MutationRequest,
  ExportK12DistrictsV2MutationResponse,
} from '../generated/types/ExportK12DistrictsV2'

// ── Internal imports for hand-written wrappers + mutations ─────────

import { getK12DistrictQueryKey as _getK12DistrictQueryKey } from '../generated/hooks/k12Controller'
import { getK12District } from '../generated/clients/getK12District'
import { bulkTagsK12Districts } from '../generated/clients/bulkTagsK12Districts'
import { bulkDistributorsK12Districts } from '../generated/clients/bulkDistributorsK12Districts'
import { bulkGposK12Districts } from '../generated/clients/bulkGposK12Districts'
import { bulkDealsK12Districts } from '../generated/clients/bulkDealsK12Districts'
import { assignK12DistrictsToCampaign } from '../generated/clients/assignK12DistrictsToCampaign'
import { exportK12DistrictsV2 } from '../generated/clients/exportK12DistrictsV2'

import type {
  GetK12DistrictQueryResponse,
  GetK12District404,
} from '../generated/types/GetK12District'
import type { BulkTagsK12DistrictsMutationRequest } from '../generated/types/BulkTagsK12Districts'
import type { BulkDistributorsK12DistrictsMutationRequest } from '../generated/types/BulkDistributorsK12Districts'
import type { BulkGposK12DistrictsMutationRequest } from '../generated/types/BulkGposK12Districts'
import type { BulkDealsK12DistrictsMutationRequest } from '../generated/types/BulkDealsK12Districts'
import type { AssignK12DistrictsToCampaignMutationRequest } from '../generated/types/AssignK12DistrictsToCampaign'
import type { ExportK12DistrictsV2MutationRequest } from '../generated/types/ExportK12DistrictsV2'

// ── Hand-written query hooks ───────────────────────────────────────

/**
 * Get K-12 district detail.
 *
 * Hand-written wrapper around the generated `getK12District` client
 * because the legacy hook (`k12api.useGetK12District`) skips retries on
 * 404 — without that predicate, React Query retries 3× before
 * surrendering, which delays "not found" feedback in the UI. The
 * wrapper accepts an optional id (so callers can pass `undefined`
 * before route resolution) and disables the query until the id arrives.
 */
export function useGetK12District(
  id?: number,
  options: {
    query?: Omit<
      UseQueryOptions<
        GetK12DistrictQueryResponse,
        AxiosError<GetK12District404>
      >,
      'queryKey' | 'queryFn' | 'enabled' | 'retry'
    >
  } = {}
) {
  const { query = {} } = options
  return useQuery<GetK12DistrictQueryResponse, AxiosError<GetK12District404>>({
    queryKey:
      id !== undefined ? _getK12DistrictQueryKey({ id }) : ['k12district', id],
    queryFn: async ({ signal }) =>
      getK12District({ id: id as number }, { signal }),
    enabled: id !== undefined,
    retry: (failureCount, error) =>
      axios.isAxiosError(error) && error?.response?.status === 404
        ? false
        : failureCount < 3,
    ...query,
  })
}

// ── Mutation hooks with auto-invalidation (WRITE) ──────────────────

export function useBulkTagsK12Districts() {
  return useInvalidatingMutation('bulkTagsK12Districts', invalidationRegistry, {
    mutationFn: (variables: { data: BulkTagsK12DistrictsMutationRequest }) =>
      bulkTagsK12Districts(variables.data),
  })
}

export function useBulkDistributorsK12Districts() {
  return useInvalidatingMutation(
    'bulkDistributorsK12Districts',
    invalidationRegistry,
    {
      mutationFn: (variables: {
        data: BulkDistributorsK12DistrictsMutationRequest
      }) => bulkDistributorsK12Districts(variables.data),
    }
  )
}

export function useBulkGposK12Districts() {
  return useInvalidatingMutation('bulkGposK12Districts', invalidationRegistry, {
    mutationFn: (variables: { data: BulkGposK12DistrictsMutationRequest }) =>
      bulkGposK12Districts(variables.data),
  })
}

export function useBulkDealsK12Districts() {
  return useInvalidatingMutation(
    'bulkDealsK12Districts',
    invalidationRegistry,
    {
      mutationFn: (variables: { data: BulkDealsK12DistrictsMutationRequest }) =>
        bulkDealsK12Districts(variables.data),
    }
  )
}

export function useAssignK12DistrictsToCampaign() {
  return useInvalidatingMutation(
    'assignK12DistrictsToCampaign',
    invalidationRegistry,
    {
      mutationFn: (variables: {
        data: AssignK12DistrictsToCampaignMutationRequest
      }) => assignK12DistrictsToCampaign(variables.data),
    }
  )
}

export function useExportK12DistrictsV2() {
  return useInvalidatingMutation('exportK12DistrictsV2', invalidationRegistry, {
    mutationFn: (variables: { data: ExportK12DistrictsV2MutationRequest }) =>
      exportK12DistrictsV2(variables.data),
  })
}
